/**
 * PhaserGame — React component that mounts a Phaser.Game instance.
 *
 * Props:
 *   manifest   — SceneManifest object from the backend
 *   actors     — initial actor list from /session/start
 *   frame      — latest FrameData (actor_positions) from /advance
 *   sceneKey   — renderer_key selecting the Phaser scene class
 *
 * Race-condition fix:
 *   Manifest/actors/frame are stored in React refs. The Phaser scene
 *   emits 'scene-ready' after create() registers its EventBus listeners.
 *   Only then does this component push the current data. Subsequent
 *   prop changes push via the same events (listeners are already live).
 *
 * Cleanup:
 *   On unmount only the channels this component owns are removed,
 *   not the entire EventBus.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import Phaser from 'phaser';
import EventBus from './EventBus';
import RendererRegistry from './RendererRegistry';
import { useViewportOrientation, resolveOrientation } from '../context/ViewportOrientationContext';

const MANAGED_EVENTS = [
  'set-manifest', 'set-actors', 'update-frame', 'switch-camera',
  'scene-ready', 'phaser-ready', 'start-clock', 'stop-clock',
  'set-annotations', 'set-orientation',
];

const PhaserGame = ({ manifest, actors, frame, sceneKey = 'football', orientationOverride }) => {
  const gameRef = useRef(null);
  const containerRef = useRef(null);
  const manifestRef = useRef(manifest);
  const actorsRef = useRef(actors);
  const frameRef = useRef(frame);
  const sceneReadyRef = useRef(false);

  const orientationCtx = useViewportOrientation();
  const orientation = resolveOrientation(orientationOverride, orientationCtx);
  const orientationRef = useRef(orientation);
  orientationRef.current = orientation;

  manifestRef.current = manifest;
  actorsRef.current = actors;
  frameRef.current = frame;

  const pushCurrentData = useCallback(() => {
    EventBus.emit('set-orientation', orientationRef.current);
    if (manifestRef.current) {
      EventBus.emit('set-manifest', manifestRef.current);
    }
    if (actorsRef.current && actorsRef.current.length > 0) {
      EventBus.emit('set-actors', actorsRef.current);
    }
    if (frameRef.current) {
      EventBus.emit('update-frame', frameRef.current);
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const SceneClass = RendererRegistry.getScene(sceneKey);

    const config = {
      type: Phaser.AUTO,
      parent: containerRef.current,
      width: 900,
      height: 480,
      backgroundColor: '#1a3300',
      scene: [SceneClass],
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      physics: {
        default: 'arcade',
        arcade: { debug: false },
      },
    };

    sceneReadyRef.current = false;

    const onSceneReady = () => {
      sceneReadyRef.current = true;
      pushCurrentData();
    };
    EventBus.on('scene-ready', onSceneReady);

    const game = new Phaser.Game(config);
    gameRef.current = game;

    return () => {
      EventBus.off('scene-ready', onSceneReady);
      MANAGED_EVENTS.forEach((e) => EventBus.removeAll(e));
      game.destroy(true);
      gameRef.current = null;
      sceneReadyRef.current = false;
    };
  }, [sceneKey, pushCurrentData]);

  useEffect(() => {
    if (manifest && sceneReadyRef.current) {
      EventBus.emit('set-manifest', manifest);
    }
  }, [manifest]);

  useEffect(() => {
    if (actors && actors.length > 0 && sceneReadyRef.current) {
      EventBus.emit('set-actors', actors);
    }
  }, [actors]);

  useEffect(() => {
    if (frame && sceneReadyRef.current) {
      EventBus.emit('update-frame', frame);
    }
  }, [frame]);

  useEffect(() => {
    // Re-push orientation whenever it changes; the Phaser scene
    // listens on 'set-orientation' and re-draws its background +
    // re-places actor sprites.
    if (sceneReadyRef.current) EventBus.emit('set-orientation', orientation);
  }, [orientation]);

  // Keep the Phaser canvas wrapper aspect-correct for the active
  // orientation. Phaser's Scale.FIT keeps the internal 900×480 stage
  // aspect, so the wrapper just needs to match.
  const wrapperAspect = orientation === 'portrait' ? '53.3 / 100' : '100 / 53.3';
  const wrapperMaxWidth = orientation === 'portrait' ? 480 : 900;

  return (
    <div
      ref={containerRef}
      className="phaser-game"
      data-orientation={orientation}
      style={{
        width: '100%',
        maxWidth: wrapperMaxWidth,
        aspectRatio: wrapperAspect,
        margin: '0 auto',
      }}
    />
  );
};

export default PhaserGame;
